import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, getIOSVersion } from '@theme/utilities';



/** Add carousel controls when the source PDP uses a grid gallery. */
function prepareQuickShopGallery() {
  const dialog = document.getElementById('quick-add-dialog');
  const template = dialog?.querySelector('[data-quick-shop-arrows]');
  if (!dialog || !(template instanceof HTMLTemplateElement)) return;

  dialog.querySelectorAll('media-gallery > slideshow-component').forEach((slideshow) => {
    const container = slideshow.querySelector('slideshow-container');
    if (container && !slideshow.querySelector('slideshow-arrows') && slideshow.querySelectorAll('slideshow-slide').length > 1) {
      container.append(template.content.cloneNode(true));
    }
  });
}

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Document | null} */
  #cachedProductHtml = null;

  /** @type {string | null} */
  #cachedProductUrl = null;

  get cachedProductHtml() {
    return this.#cachedProductHtml;
  }

  get productPageUrl() {
    const card = this.closest('product-card, .Index_object_testimonials_innerloops_innerboxs_new');
    const link = /** @type {HTMLAnchorElement | null | undefined} */ (
      card?.querySelector('[ref="productCardLink"]') || card?.querySelector('a')
    );
    if (!link?.href) return;

    const url = new URL(link.href, window.location.href);
    // Card links can target a featured-image variant rather than the priced variant.
    const price = card?.querySelector('.Product_card_innerbottom_price_new [ref="priceContainer"][data-variant-id]')
      || card?.querySelector('product-price [ref="priceContainer"][data-variant-id]');
    const variantId = price?.getAttribute('data-variant-id');
    if (variantId) url.searchParams.set('variant', variantId);
    return url.href;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const productPageUrl = this.productPageUrl;
    if (!productPageUrl) return;
    if (!this.#cachedProductHtml || this.#cachedProductUrl !== productPageUrl) {
      await this.fetchProductPage(productPageUrl);
    }

    if (!this.#cachedProductHtml || this.#cachedProductUrl !== this.productPageUrl) return;

    if (this.#cachedProductHtml) {
      // Create a fresh copy of the cached HTML to avoid modifying the original
      const freshHtmlCopy = new DOMParser().parseFromString(
        this.#cachedProductHtml.documentElement.outerHTML,
        'text/html'
      );

      await this.updateQuickAddModal(freshHtmlCopy);
    }

    this.#openQuickAddModal();
    document.dispatchEvent(new CustomEvent('quick-atc-slider:init'));
  };

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<void>}
   * @throws {Error} If the fetch request fails or returns a non-200 response
   */
  async fetchProductPage(productPageUrl) {
    productPageUrl = this.productPageUrl || productPageUrl;
    if (!productPageUrl) return;
    if (this.#cachedProductHtml && this.#cachedProductUrl === productPageUrl) return;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    const controller = new AbortController();
    this.#abortController = controller;

    try {
      const response = await fetch(productPageUrl, {
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      // Ignore a response superseded by a newer variant request.
      if (controller.signal.aborted) return;
      this.#cachedProductHtml = html;
      this.#cachedProductUrl = productPageUrl;
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      } else {
        throw error;
      }
    } finally {
      if (this.#abortController === controller) this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   */
  async updateQuickAddModal(newHtml) {
    const productGrid = newHtml.querySelector('[data-product-grid-content]');
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    const content = document.createElement('div');
    const body = document.createElement('div');
    body.className = 'quick-shop__body';
    body.append(productGrid);
    content.append(body);

    // Move the buy-buttons block into the footer. The wishlist element is kept
    // OUT of the morph cycle and appended fresh each time so the Swish Lit
    // component always runs connectedCallback with the correct product handle.
    const buyButtons = productGrid.querySelector('.buy-buttons-block');
    /** @type {string|undefined} */
    let wishlistHandle;
    /** @type {string} */
    let wishlistVariantId = '';
    if (buyButtons) {
      const footer = document.createElement('div');
      footer.className = 'quick-shop__footer';
      footer.append(buyButtons);
      const form = buyButtons.querySelector('product-form-component');
      const productPath = form?.getAttribute('data-product-url');
      wishlistHandle = productPath?.split('/products/')[1]?.split(/[?#/]/)[0];
      const variantInput = buyButtons.querySelector('input[name="id"]');
      wishlistVariantId = variantInput instanceof HTMLInputElement ? variantInput.value : '';
      content.append(footer);
    }

    const details = productGrid.querySelector('.Product_information_rightside_loops_new > .group-block-content');
    const productLink = productGrid.querySelector('.view-product-title a');
    const productUrl = productLink?.getAttribute('href') || this.productPageUrl;
    if (details && productUrl) {
      const link = document.createElement('a');
      link.className = 'hb_view-full-details';
      link.href = productUrl;
      link.textContent = document.getElementById('quick-add-dialog')?.dataset.detailsLabel || 'View full detail';
      details.querySelectorAll('.hb_view-full-details, .view-more-details__wrapper').forEach((element) => element.remove());
      const description = details.querySelector('.Product_short_description_main_new');
      if (description) description.after(link);
      else details.append(link);
    }

    // Grab the features block directly from the fetched product page HTML,
    // exactly as it is rendered on the PDP overview tab.
    const featuresBlock = newHtml.querySelector('.Product_object_meta_tabs_block_innerblock_innerdetail_innerleft_boths_new');
    if (featuresBlock) {
      const anchor = productGrid.querySelector('.Product_claims_icons_main_new')
        || productGrid.querySelector('.Product_icons_with_texts_main_new');
      anchor?.after(featuresBlock);
    }

    // The PDP's secondary sticky cart and benefit slider are not used in the drawer.
    productGrid.querySelectorAll('.sticky_addtocart_new').forEach((element) => element.remove());
    productGrid.querySelectorAll('[hb-text-icon-swiper-container]').forEach((element) => {
      element.removeAttribute('hb-text-icon-swiper-container');
    });

    const reviewWidget = productGrid.querySelector('.Product_yotpo_review_main_new .yotpo-widget-instance');
    if (reviewWidget) reviewWidget.id = 'quick-shop-yotpo-stars';

    // Update drawer title based on whether the product has selectable variants.
    // productGrid has already been detached from newHtml, so query it directly.
    const hasVariants = !!productGrid.querySelector(
      'variant-picker fieldset, variant-picker select'
    );
    const titleEl = document.getElementById('quick-shop-title');
    if (titleEl) titleEl.textContent = hasVariants ? 'Choose variant' : 'Quick shop';

    morph(modalContent, content);

    // Rebuild the wishlist element after every morph so Swish always fires
    // connectedCallback with the correct product handle and variant ID.
    const footer = modalContent.querySelector('.quick-shop__footer');
    if (footer && wishlistHandle) {
      footer.querySelectorAll('.quick-shop__wishlist').forEach((el) => el.remove());
      const wishlist = document.createElement('wishlist-button-product');
      wishlist.className = 'quick-shop__wishlist';
      wishlist.dataset.productHandle = wishlistHandle;
      wishlist.dataset.variantId = wishlistVariantId;
      wishlist.setAttribute('show-icon', '');
      wishlist.setAttribute('align-content', 'center');
      footer.append(wishlist);
    }

    prepareQuickShopGallery();
    // Fetched HTML contains only Yotpo placeholders, not the rendered rating.
    const reviewApps = /** @type {Window & {
      yotpoWidgetsContainer?: { initWidgets: () => void },
      yotpo?: { initialized: boolean, refreshWidgets: () => void }
    }} */ (window);
    try {
      if (reviewApps.yotpoWidgetsContainer?.initWidgets) {
        reviewApps.yotpoWidgetsContainer.initWidgets();
      } else if (reviewApps.yotpo?.initialized) {
        reviewApps.yotpo.refreshWidgets();
      }
    } catch (error) {
      console.warn('[Quick shop] Unable to initialise review ratings', error);
    }
    const scrollBody = modalContent.querySelector('.quick-shop__body');
    if (scrollBody) scrollBody.scrollTop = 0;
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();
  #reviewObserver = new MutationObserver(() => this.#formatReviewCounts());

  #formatReviewCounts() {
    this.querySelectorAll('.Product_yotpo_review_infos_new').forEach((reviews) => {
      const count = reviews.querySelector('.yotpo-sr-bottom-line-text') ||
        reviews.querySelector('.yotpo-sr-bottom-line-right-panel p');
      const number = count?.textContent?.match(/\d[\d,.\u00a0\u202f]*/)?.[0];
      if (!count || !number) return;
      const formatted = `(${number})`;
      // Yotpo renders asynchronously and may replace its text after a variant change.
      if (count.textContent !== formatted) count.textContent = formatted;
    });
  }

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    this.#reviewObserver.observe(this, { childList: true, subtree: true, characterData: true });
    this.#formatReviewCounts();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.#reviewObserver.disconnect();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    this.removeEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    prepareQuickShopGallery();
    const footer = this.querySelector('.quick-shop__footer');
    const newForm = event.detail.data.html?.querySelector('product-form-component');
    if (footer && newForm) {
      const handle = newForm.dataset.productUrl?.split('/products/')[1]?.split(/[?#/]/)[0];
      const variantInput = newForm.querySelector('input[name="id"]');
      const variantId = variantInput instanceof HTMLInputElement ? variantInput.value : '';
      if (handle) {
        footer.querySelectorAll('.quick-shop__wishlist').forEach((el) => el.remove());
        const wishlist = document.createElement('wishlist-button-product');
        wishlist.className = 'quick-shop__wishlist';
        wishlist.dataset.productHandle = handle;
        wishlist.dataset.variantId = variantId;
        wishlist.setAttribute('show-icon', '');
        wishlist.setAttribute('align-content', 'center');
        footer.append(wishlist);
      }
    }
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    const quickShopDetailsLink = this.querySelector('.hb_view-full-details');
    if (quickShopDetailsLink instanceof HTMLAnchorElement) quickShopDetailsLink.href = anchorElement.href;
    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}  